import { FormatBold, FormatColorFill, FormatColorText, FormatItalic, FormatUnderlined, StrikethroughS, Subscript, Superscript } from "@mui/icons-material";
import { Box, IconButton, Popover, Stack, Tooltip } from "@mui/material";
import { Editor } from "@tiptap/react";
import { useEffect, useState } from "react";

interface NoteTextDesignProps {
    editor: Editor;
    mode: number;
}

const COLORS = [
    '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
    '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
    '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b266', '#66a3e0', '#c285ff',
    '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
    '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466'
];

export default function NoteTextDesign({ editor, mode }: NoteTextDesignProps) {
    const [formats, setFormats] = useState({
        bold: false,
        italic: false,
        underline: false,
        strike: false,
        subscript: false,
        superscript: false,
        highlight: false,
    });

    const [textSelectedColor, setTextSelectedColor] = useState<string>(COLORS[0]);
    const [highlightSelectedColor, setHighlightSelectedColor] = useState<string>(COLORS[0]);

    const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
    const [highlightAnchorEl, setHighlightAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const updateFormats = () => {
            setFormats({
                bold: editor.isActive('bold'),
                italic: editor.isActive('italic'),
                underline: editor.isActive('underline'),
                strike: editor.isActive('strike'),
                subscript: editor.isActive('subscript'),
                superscript: editor.isActive('superscript'),
                highlight: editor.isActive('highlight'),
            });
        };

        editor.on('selectionUpdate', updateFormats);
        
        return () => {
            editor.off('selectionUpdate', updateFormats);
        };
    }, [editor]);

    const toggleFormat = (format: keyof typeof formats, command: () => void) => {
        command();
        setFormats((prev) => ({ ...prev, [format]: !prev[format] }));
    };

    const handleColorChange = (color: string) => {
        editor.chain().focus().setColor(color).run();
        setTextSelectedColor(color);
        setColorAnchorEl(null);
    };

    const handleHighlightChange = (color: string) => {
        editor.chain().focus().toggleHighlight({ color }).run();
        setHighlightSelectedColor(color);
        setHighlightAnchorEl(null);
    };

    return (
        <>
            <Stack direction="row">
                <Tooltip title="Bold">
                    <IconButton
                        size="small"
                        onClick={() => toggleFormat('bold', () => editor.chain().focus().toggleBold().run())}
                        sx={{
                            backgroundColor: formats.bold ? '#e8f0fe' : 'transparent',
                            color: formats.bold ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white'),
                        }}
                    >
                        <FormatBold sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Italic">
                    <IconButton
                        size="small"
                        onClick={() => toggleFormat('italic', () => editor.chain().focus().toggleItalic().run())}
                        sx={{
                            backgroundColor: formats.italic ? '#e8f0fe' : 'transparent',
                            color: formats.italic ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white'),
                        }}
                    >
                        <FormatItalic sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Underline">
                    <IconButton
                        size="small"
                        onClick={() => toggleFormat('underline', () => editor.chain().focus().toggleUnderline().run())}
                        sx={{
                            backgroundColor: formats.underline ? '#e8f0fe' : 'transparent',
                            color: formats.underline ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white'),
                        }}
                    >
                        <FormatUnderlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Strikethrough">
                    <IconButton
                        size="small"
                        onClick={() => toggleFormat('strike', () => editor.chain().focus().toggleStrike().run())}
                        sx={{
                            backgroundColor: formats.strike ? '#e8f0fe' : 'transparent',
                            color: formats.strike ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white'),
                        }}
                    >
                        <StrikethroughS sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Subscript">
                    <IconButton
                        size="small"
                        onClick={() => toggleFormat('subscript', () => editor.chain().focus().toggleSubscript().run())}
                        sx={{
                            backgroundColor: formats.subscript ? '#e8f0fe' : 'transparent',
                            color: formats.subscript ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white'),
                        }}
                    >
                        <Subscript sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Superscript">
                    <IconButton
                        size="small"
                        onClick={() => toggleFormat('superscript', () => editor.chain().focus().toggleSuperscript().run())}
                        sx={{
                            backgroundColor: formats.superscript ? '#e8f0fe' : 'transparent',
                            color: formats.superscript ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white'),
                        }}
                    >
                        <Superscript sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Text color">
                    <IconButton
                        size="small"
                        onClick={(e) => setColorAnchorEl(e.currentTarget)}
                        sx={{
                            color: textSelectedColor,
                        }}
                    >
                        <FormatColorText sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Highlight color">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            if (formats.highlight) {
                                editor.chain().focus().unsetHighlight().run();
                                setFormats((prev) => ({ ...prev, highlight: false }));
                            } else {
                                setHighlightAnchorEl(e.currentTarget);
                            }
                        }}
                        sx={{
                            backgroundColor: formats.highlight ? '#e8f0fe' : 'transparent',
                            color: formats.highlight ? highlightSelectedColor : 'inherit',
                        }}
                    >
                        <FormatColorFill sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Popover
                open={Boolean(colorAnchorEl)}
                anchorEl={colorAnchorEl}
                onClose={() => setColorAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                    {COLORS.map((color) => (
                        <Box
                            key={color}
                            onClick={() => handleColorChange(color)}
                            sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: color,
                                border: '1px solid #ccc',
                                cursor: 'pointer',
                                borderRadius: '2px',
                                '&:hover': { transform: 'scale(1.1)' },
                            }}
                        />
                    ))}
                </Box>
            </Popover>

            <Popover
                open={Boolean(highlightAnchorEl)}
                anchorEl={highlightAnchorEl}
                onClose={() => setHighlightAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                    {COLORS.map((color) => (
                        <Box
                            key={color}
                            onClick={() => handleHighlightChange(color)}
                            sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: color,
                                border: '1px solid #ccc',
                                cursor: 'pointer',
                                borderRadius: '2px',
                                '&:hover': { transform: 'scale(1.1)' },
                            }}
                        />
                    ))}
                </Box>
            </Popover>
        </>
    );
}
